// A staff-run station's countdown screen (ADR-0033, spec/13 §3.3): a public, read-only page for a TV or tablet beside a
// console, opened from the link staff show in the station modal (`/display/{token}`). No sign-in and no device
// identity: the token is the credential, scoped to one station, and staff can rotate or revoke it at any time.
//
// It shows the station, the venue's name and logo, and — while a session runs — the player's first name or gamer tag,
// a big countdown, what comes after it ("then 5 min of grace"), and the game staff picked. Nothing it shows names an
// organisation, a station id or a session; the api sends no more than this (`GET /v1/public/displays/{token}`).
//
// **Live, with a fallback.** A display-scoped realtime socket (`kind: display`, its one channel `display:{stationId}`)
// carries `display.changed` — "re-read", no data — so a start, time sold, a lock or the edge into grace shows within a
// second. When the socket is down the page polls every 15 seconds (every minute while it is live). The countdown ticks
// every second against the api's `serverTime`, as every clock in the product does (spec/05 §4.4).
//
// Adding time or ordering food is not offered here — a page with no device identity spends no one's money — so it
// shows a QR to the player app instead. A revoked link reads "This screen is no longer linked".

import type { StationDisplayView } from '@clavian/api-client';
import { DISPLAY_CHANGED, STATION_DISPLAY_POLL_SECONDS } from '@clavian/contracts/constants';
import { useTranslation } from '@clavian/i18n/react';
import { cx, encodeQr, qrPath } from '@clavian/ui-web';
import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { useApi } from '../api/client';
import { PROBLEM, problemCode } from '../api/problems';
import { createRealtimeConnection, type RealtimeStatus } from '../realtime/connection';
import { resolveRealtimeUrl } from '../realtime/RealtimeProvider';

import './display.css';

/** Whole seconds as `H:MM:SS`, or `MM:SS` under an hour — the big clock. Never below zero. */
export function countdownOf(totalSeconds: number): string {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = seconds % 60;
  const mm = String(minutes).padStart(2, '0');
  const ss = String(rest).padStart(2, '0');
  return hours > 0 ? `${hours}:${mm}:${ss}` : `${mm}:${ss}`;
}

/**
 * The seconds the screen shows now: the funded time left counting down from when it was read (or, in grace, the grace
 * left). A held session's clock does not tick between reads — the next read says where it stands.
 */
export function displaySecondsNow(session: NonNullable<StationDisplayView['session']>, serverTime: string, readAt: number, now: number): number {
  if (session.status === 'grace') {
    if (session.graceEndsAt === undefined) return 0;
    const offset = readAt - Date.parse(serverTime);
    return Math.max(0, Math.floor((Date.parse(session.graceEndsAt) + offset - now) / 1000));
  }
  if (session.status === 'locked') return session.timeLeftSeconds;
  return Math.max(0, session.timeLeftSeconds - Math.max(0, Math.floor((now - readAt) / 1000)));
}

/** The display's own realtime socket: `display.changed` asks for a re-read. Returns where the socket stands. */
function useDisplayLive(token: string, enabled: boolean, onChanged: () => void): RealtimeStatus {
  const api = useApi();
  const [status, setStatus] = useState<RealtimeStatus>('offline');
  useEffect(() => {
    if (!enabled || typeof WebSocket === 'undefined') return undefined;
    const connection = createRealtimeConnection({
      url: resolveRealtimeUrl(),
      fetchToken: () => api.createPublicDisplayRealtimeToken({ path: { token } }),
      onOwnEvent: (envelope) => {
        if (envelope.type === DISPLAY_CHANGED) onChanged();
      },
    });
    const unwatch = connection.watch(setStatus);
    connection.start();
    return () => {
      unwatch();
      connection.stop();
    };
  }, [api, token, enabled, onChanged]);
  return status;
}

export function DisplayPage(): ReactNode {
  const { token } = useParams({ strict: false }) as { token: string };
  return <DisplayScreen token={token} />;
}

export function DisplayScreen({ token }: { readonly token: string }): ReactNode {
  const { t } = useTranslation('console');
  const api = useApi();
  // Where the display's socket stands: polled every 15 seconds while it is down, every minute while it is live.
  const liveStatus = useRef<RealtimeStatus>('offline');
  const read = useQuery({
    queryKey: ['public-display', token],
    queryFn: () => api.getPublicDisplay({ path: { token } }),
    refetchInterval: () => (liveStatus.current === 'live' ? 60 : STATION_DISPLAY_POLL_SECONDS) * 1000,
    refetchOnWindowFocus: true,
    // A revoked or unknown link is an answer, not a blip: no retries.
    retry: (failures, error) => problemCode(error) !== PROBLEM.notFound && failures < 3,
  });
  const unlinked = read.isError && problemCode(read.error) === PROBLEM.notFound;
  const refetchRef = useRef(read.refetch);
  useEffect(() => {
    refetchRef.current = read.refetch;
  }, [read.refetch]);
  const onChanged = useCallback(() => void refetchRef.current(), []);
  const status = useDisplayLive(token, !unlinked, onChanged);
  useEffect(() => {
    liveStatus.current = status;
  }, [status]);

  // The big clock ticks every second.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    document.title = read.data === undefined ? t('display.title') : `${read.data.station.name ?? `#${read.data.station.number}`} · ${t('display.title')}`;
  }, [read.data, t]);

  // A countdown screen is a TV at a station, never the console: it stays dark whatever theme a member chose on
  // this browser (ADR-0041), so each of its states pins `data-theme="dark"`.
  if (unlinked) {
    return (
      <main className="console-display console-display--message" data-theme="dark">
        <p className="console-display__message">{t('display.unlinked')}</p>
        <p className="console-display__sub">{t('display.unlinkedHint')}</p>
      </main>
    );
  }
  const view = read.data;
  if (view === undefined) {
    return (
      <main className="console-display console-display--message" data-theme="dark" aria-busy="true">
        <p className="console-display__sub">{read.isError ? t('display.failed') : t('display.loading')}</p>
      </main>
    );
  }
  return <DisplayBody view={view} readAt={read.dataUpdatedAt} now={now} reconnecting={read.isError} />;
}

function DisplayBody({ view, readAt, now, reconnecting }: { readonly view: StationDisplayView; readonly readAt: number; readonly now: number; readonly reconnecting: boolean }): ReactNode {
  const { t } = useTranslation('console');
  const session = view.session;
  const stationName = view.station.name ?? `${t(`stations.kind.${view.station.kind}`)} ${view.station.number}`;
  const seconds = session === null ? 0 : displaySecondsNow(session, view.serverTime, readAt, now);
  // An Open console session (ADR-0034): no end — the clock counts the time played up from its start.
  const open = session !== null && session.open === true && session.status !== 'grace';
  const played = open && session.startedAt !== undefined ? Math.max(0, Math.floor((now - readAt + Date.parse(view.serverTime) - Date.parse(session.startedAt)) / 1000)) : 0;
  const graceMinutes = session === null ? 0 : Math.round(session.graceSeconds / 60);
  const state = session === null ? 'free' : session.status;

  return (
    <main className={cx('console-display', `console-display--${state}`)} data-theme="dark">
      <header className="console-display__top">
        {view.venue.logoUrl === null ? (
          <span className="console-display__venue">{view.venue.name}</span>
        ) : (
          <img className="console-display__logo" src={view.venue.logoUrl} alt={view.venue.name} />
        )}
        <span className="console-display__station">
          <span className="console-display__number">{view.station.number}</span>
          {stationName}
        </span>
      </header>

      <section className="console-display__center" aria-live="polite">
        {session === null ? (
          <>
            <p className="console-display__state">{t('display.free')}</p>
            <p className="console-display__sub">{t('display.freeHint')}</p>
          </>
        ) : (
          <>
            {session.playerLabel === null ? null : <p className="console-display__hello">{t('display.hello', { name: session.playerLabel })}</p>}
            {session.status === 'grace' ? <p className="console-display__state">{t('display.grace')}</p> : null}
            {session.status === 'locked' ? <p className="console-display__state">{t('display.locked')}</p> : null}
            <p className="console-display__clock" role="timer" aria-label={open ? `${countdownOf(played)} ${t('display.played')}` : `${countdownOf(seconds)} ${t('display.left')}`}>
              {countdownOf(open ? played : seconds)}
            </p>
            <p className="console-display__sub">
              {open
                ? t('display.openHint')
                : session.status === 'grace'
                ? t('display.graceHint', { time: countdownOf(seconds) })
                : session.status === 'locked'
                  ? t('display.lockedHint')
                  : graceMinutes > 0
                    ? `${t('display.left')} · ${t('display.thenGrace', { minutes: graceMinutes })}`
                    : t('display.left')}
            </p>
            {session.title === null ? null : (
              <p className="console-display__title">
                <span className="console-display__eyebrow">{t('display.playing')}</span> {session.title}
              </p>
            )}
          </>
        )}
      </section>

      <footer className="console-display__foot">
        <PlayerAppQr url={view.playerAppUrl} />
        <p className="console-display__phone">{t('display.phone')}</p>
        {reconnecting ? <p className="console-display__offline">{t('display.offline')}</p> : null}
      </footer>
    </main>
  );
}

function PlayerAppQr({ url }: { readonly url: string }): ReactNode {
  const code = useMemo(() => {
    try {
      return encodeQr(url);
    } catch {
      return null;
    }
  }, [url]);
  if (code === null) return null;
  return (
    <span className="console-display__qr" aria-hidden="true">
      <svg viewBox={`-4 -4 ${code.size + 8} ${code.size + 8}`} shapeRendering="crispEdges" focusable="false">
        <rect x="-4" y="-4" width={code.size + 8} height={code.size + 8} fill="#fff" />
        <path d={qrPath(code)} fill="#000" />
      </svg>
    </span>
  );
}
